import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, InjectionToken, computed, inject, input } from '@angular/core';
import { HxLoader } from '../../controls/loader/loader';
import { GlobalLoadingService, LoadingContext } from '../../services/global-loading.service';

/**
 * Injection token to control whether the global loader shows contextual status messages. When enabled, displays the
 * current loading message/reason beneath the animated loader.
 */
export const SHOW_GLOBAL_LOADER_MESSAGE = new InjectionToken<boolean>('SHOW_GLOBAL_LOADER_MESSAGE', {
  providedIn: 'root',
  factory: () => false,
});

/**
 * Global Loader Overlay Component
 *
 * Full-screen loading overlay that displays the HxLoader component with company icon logo. Automatically shows/hides
 * based on GlobalLoadingService state.
 *
 * This component should be placed at the root level of your application, typically in the app/entry component template.
 *
 * Features:
 *
 * - Full-screen overlay with semi-transparent background
 * - Large spinning logo animation
 * - Optional loading message display
 * - Automatic show/hide via GlobalLoadingService
 * - Accessible ARIA attributes
 *
 * @example
 *   ```html
 *   <!-- In your app/entry component -->
 *   <router-outlet></router-outlet>
 *   <hx-global-loader />
 *   ```;
 */
@Component({
  selector: 'hx-global-loader',
  standalone: true,
  imports: [CommonModule, HxLoader],
  templateUrl: './global-loader.html',
  styleUrls: ['./global-loader.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxGlobalLoader {
  protected readonly loadingService = inject(GlobalLoadingService);
  private readonly defaultShowMessage = inject(SHOW_GLOBAL_LOADER_MESSAGE, { optional: true }) ?? false;

  /** Override injection token via input property if specified directly on the element */
  readonly showMessage = input<boolean | undefined>(undefined);

  /**
   * Effective flag whether to display loading message (checks input override -> dynamic service signal -> token
   * default)
   */
  protected readonly isMessageEnabled = computed(
    () => this.showMessage() ?? (this.loadingService.isDebugMessageEnabled() || this.defaultShowMessage)
  );

  private formatContext(ctx: LoadingContext): string {
    if (ctx.message) {
      return ctx.message;
    }
    if (ctx.feature && ctx.action) {
      return `[${ctx.feature}] ${ctx.action}`;
    }
    if (ctx.method && ctx.url) {
      return `[${ctx.method.toUpperCase()}] ${ctx.url}`;
    }
    if (ctx.feature) {
      return `[${ctx.feature}]`;
    }
    return 'Active request';
  }

  /** Primary single-line text displayed in the message badge */
  protected readonly primaryMessage = computed(() => {
    if (!this.isMessageEnabled()) {
      return undefined;
    }

    const ctx = this.loadingService.context();
    const msg = this.loadingService.message();

    if (msg) {
      return msg;
    }
    if (ctx) {
      return this.formatContext(ctx);
    }
    return undefined;
  });

  /** Additional requests count (if more than 1 request is active) */
  protected readonly additionalCount = computed(() => {
    const contexts = this.loadingService.contexts();
    const total = contexts ? contexts.length : 0;
    return total > 1 ? total - 1 : 0;
  });

  /** Formatted list of all active operation strings for the popover card */
  protected readonly activeCallsList = computed(() => {
    const contexts = this.loadingService.contexts();
    if (!contexts || contexts.length === 0) {
      return [];
    }

    return contexts.map((ctx) => this.formatContext(ctx));
  });
}
